/**
 * Sprint 13B — Raw Material Flow Report
 *
 * Shows the complete cotton-to-yarn funnel for a plant/period:
 *   Received → QC Approved → QC Rejected → Issued → Returned → Net Consumed → Yarn Produced
 *
 * Plus:
 *  - Effective realization %
 *  - By-supplier breakdown table
 *  - By-variety breakdown table
 */
import React, { useState } from 'react'
import {
  Alert, Button, Card, Col, DatePicker, Divider, Row,
  Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FilterOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

// ── Types ─────────────────────────────────────────────────────────────────────
interface FlowStage {
  bales?: number
  kg: number
  lots?: number
}

interface RMFlowSummary {
  period: { from_date: string | null; to_date: string | null }
  funnel: {
    received: FlowStage
    qc_approved: FlowStage
    qc_rejected: FlowStage
    in_quarantine: FlowStage
    issued: FlowStage
    returned: FlowStage
    net_consumed: FlowStage
    yarn_produced: FlowStage & { lots: number }
  }
  realization_pct: number
  supplier_breakdown: Array<{
    supplier_name: string
    bale_count: number
    weight_kg: number
    avg_mic: number | null
    avg_uhml: number | null
  }>
  variety_breakdown: Array<{
    variety: string
    bale_count: number
    weight_kg: number
    pct: number
  }>
}

// ── API ───────────────────────────────────────────────────────────────────────
function fetchRMFlow(plant_id: string, from_date?: string, to_date?: string): Promise<RMFlowSummary> {
  return apiClient.get('/traceability/rm-flow-summary', {
    params: { plant_id, from_date, to_date },
  }).then(r => r.data)
}

// ── Funnel Card ───────────────────────────────────────────────────────────────
function FunnelCard({
  label, bales, kg, lots, color, icon, percent,
}: {
  label: string
  bales?: number
  kg: number
  lots?: number
  color: string
  icon?: React.ReactNode
  percent?: number
}) {
  return (
    <Card
      size="small"
      style={{
        borderTop: `3px solid ${color}`,
        textAlign: 'center',
        height: 120,
      }}
    >
      <div style={{ color, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {icon && <span style={{ marginRight: 4 }}>{icon}</span>}
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
        {kg.toLocaleString('en-IN', { maximumFractionDigits: 0 })} kg
      </div>
      {bales !== undefined && (
        <div style={{ fontSize: 12, color: '#888' }}>
          {bales.toLocaleString()} bales
        </div>
      )}
      {lots !== undefined && (
        <div style={{ fontSize: 12, color: '#888' }}>
          {lots.toLocaleString()} lots
        </div>
      )}
      {percent !== undefined && (
        <div style={{ fontSize: 11, color: percent >= 85 ? '#52c41a' : percent >= 80 ? '#fa8c16' : '#ff4d4f' }}>
          {percent.toFixed(2)}% realization
        </div>
      )}
    </Card>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function RMFlowReportPage() {
  const { plantId } = usePlantId()
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [applied, setApplied] = useState<{ from?: string; to?: string }>({})

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['rm-flow-summary', plantId, applied.from, applied.to],
    queryFn: () => fetchRMFlow(plantId!, applied.from, applied.to),
    enabled: !!plantId,
  })

  const handleApply = () => {
    setApplied({
      from: dateRange[0]?.format('YYYY-MM-DD'),
      to: dateRange[1]?.format('YYYY-MM-DD'),
    })
  }

  const funnel = data?.funnel

  // ── Supplier table ────────────────────────────────────────────────────────
  const supplierCols: ColumnsType<RMFlowSummary['supplier_breakdown'][0]> = [
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Bales',
      dataIndex: 'bale_count',
      key: 'bale_count',
      align: 'right',
      sorter: (a, b) => a.bale_count - b.bale_count,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      align: 'right',
      sorter: (a, b) => a.weight_kg - b.weight_kg,
      render: (v: number) => v.toLocaleString('en-IN', { maximumFractionDigits: 0 }),
    },
    {
      title: 'Share %',
      key: 'share',
      align: 'right',
      render: (_: unknown, row) => {
        const total = data?.funnel.received.kg ?? 0
        if (!total) return '—'
        return `${((row.weight_kg / total) * 100).toFixed(1)}%`
      },
    },
    {
      title: 'Avg MIC',
      dataIndex: 'avg_mic',
      key: 'avg_mic',
      align: 'right',
      render: (v: number | null) => v?.toFixed(2) ?? '—',
    },
    {
      title: 'Avg UHML (mm)',
      dataIndex: 'avg_uhml',
      key: 'avg_uhml',
      align: 'right',
      render: (v: number | null) => v?.toFixed(2) ?? '—',
    },
  ]

  // ── Variety table ──────────────────────────────────────────────────────────
  const varietyCols: ColumnsType<RMFlowSummary['variety_breakdown'][0]> = [
    {
      title: 'Cotton Variety',
      dataIndex: 'variety',
      key: 'variety',
      render: (v: string) => <Tag color="purple">{v}</Tag>,
    },
    { title: 'Bales', dataIndex: 'bale_count', key: 'bale_count', align: 'right' },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      align: 'right',
      render: (v: number) => v.toLocaleString('en-IN', { maximumFractionDigits: 0 }),
    },
    {
      title: 'Share %',
      dataIndex: 'pct',
      key: 'pct',
      align: 'right',
      render: (v: number) => `${v.toFixed(1)}%`,
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <ApartmentOutlined style={{ marginRight: 8 }} />
            Raw Material Flow Report
          </Title>
          <Text type="secondary">Cotton → QC → Issue → Production → Yarn funnel</Text>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 20 }}>
        <Space wrap>
          <FilterOutlined />
          <Text>Period:</Text>
          <RangePicker
            value={dateRange}
            onChange={v => setDateRange(v as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
            allowClear
          />
          <Button type="primary" onClick={handleApply} loading={isFetching}>
            Apply
          </Button>
          <Button onClick={() => { setDateRange([null, null]); setApplied({}) }}>
            Clear
          </Button>
        </Space>
      </Card>

      {/* Realization Alert */}
      {data && (
        <>
          {data.realization_pct < 80 && data.realization_pct > 0 && (
            <Alert
              type="error" showIcon icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
              message={`Critical: Effective realization is only ${data.realization_pct}% — well below the 84% threshold. Investigate waste and losses.`}
            />
          )}
          {data.realization_pct >= 80 && data.realization_pct < 85 && (
            <Alert
              type="warning" showIcon
              style={{ marginBottom: 16 }}
              message={`Warning: Realization at ${data.realization_pct}% — below the 86% standard. Review stage-wise waste.`}
            />
          )}
        </>
      )}

      {/* Funnel row */}
      <Row gutter={8} align="middle" style={{ marginBottom: 24 }} wrap>
        <Col xs={24} sm={11} md={5}>
          <FunnelCard
            label="Received"
            bales={funnel?.received.bales}
            kg={funnel?.received.kg ?? 0}
            color="#1890ff"
          />
        </Col>
        <Col xs={0} sm={1} style={{ textAlign: 'center' }}>
          <ArrowRightOutlined style={{ color: '#aaa', fontSize: 18 }} />
        </Col>
        <Col xs={24} sm={11} md={4}>
          <FunnelCard
            label="QC Approved"
            bales={funnel?.qc_approved.bales}
            kg={funnel?.qc_approved.kg ?? 0}
            color="#52c41a"
            icon={<CheckCircleOutlined />}
          />
        </Col>
        <Col xs={0} sm={1} style={{ textAlign: 'center' }}>
          <ArrowRightOutlined style={{ color: '#aaa', fontSize: 18 }} />
        </Col>
        <Col xs={24} sm={11} md={4}>
          <FunnelCard
            label="Issued to B/R"
            bales={funnel?.issued.bales}
            kg={funnel?.issued.kg ?? 0}
            color="#722ed1"
          />
        </Col>
        <Col xs={0} sm={1} style={{ textAlign: 'center' }}>
          <ArrowRightOutlined style={{ color: '#aaa', fontSize: 18 }} />
        </Col>
        <Col xs={24} sm={11} md={4}>
          <FunnelCard
            label="Net Consumed"
            bales={funnel?.net_consumed.bales}
            kg={funnel?.net_consumed.kg ?? 0}
            color="#fa8c16"
          />
        </Col>
        <Col xs={0} sm={1} style={{ textAlign: 'center' }}>
          <ArrowRightOutlined style={{ color: '#aaa', fontSize: 18 }} />
        </Col>
        <Col xs={24} sm={11} md={4}>
          <FunnelCard
            label="Yarn Produced"
            lots={funnel?.yarn_produced.lots}
            kg={funnel?.yarn_produced.kg ?? 0}
            color="#52c41a"
            percent={data?.realization_pct}
          />
        </Col>
      </Row>

      {/* Secondary KPIs */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="QC Rejected"
              value={funnel?.qc_rejected.kg.toFixed(0)}
              suffix="kg"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="In Quarantine"
              value={funnel?.in_quarantine.kg.toFixed(0)}
              suffix="kg"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Returned from B/R"
              value={funnel?.returned.kg.toFixed(0)}
              suffix="kg"
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Effective Realization"
              value={data?.realization_pct.toFixed(2) ?? '—'}
              suffix="%"
              valueStyle={{
                color: !data ? '#888'
                  : data.realization_pct >= 85 ? '#52c41a'
                  : data.realization_pct >= 80 ? '#fa8c16'
                  : '#cf1322',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Supplier Breakdown */}
      <Card
        title="Supplier-wise Breakdown"
        size="small"
        style={{ marginBottom: 20 }}
        extra={<Tag color="blue">{data?.supplier_breakdown.length ?? 0} suppliers</Tag>}
      >
        <Table
          rowKey="supplier_name"
          dataSource={data?.supplier_breakdown ?? []}
          columns={supplierCols}
          loading={isLoading}
          pagination={false}
          size="small"
          bordered
        />
      </Card>

      {/* Variety Breakdown */}
      <Card
        title="Cotton Variety Mix"
        size="small"
        extra={<Tag color="purple">{data?.variety_breakdown.length ?? 0} varieties</Tag>}
      >
        <Table
          rowKey="variety"
          dataSource={data?.variety_breakdown ?? []}
          columns={varietyCols}
          loading={isLoading}
          pagination={false}
          size="small"
          bordered
        />
      </Card>
    </div>
  )
}
